import React, { useState, useContext, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import CarteQuestion from './carteQuestion';
import CarteNewQuestion from './carteNewQuestion';
import CarteStrategie from './carteStrategie';
import { LOCALES } from '../../i18n';
import { AppContext } from '../context';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import translate from '../../i18n/translate';
import { switchOrderReponse } from '../../services/requestHandler';
import { KeycloackContext } from '../../services/keycloakProvider';

export default function QuestionReponseForm({
    questionReponses = [],
    strategieReponses = [],
    stateCheminDone = false,
    needReloadComponent,
    reloadComponent
}) {
    const { state } = useContext(AppContext);
    const { isAdminRole } = useContext(KeycloackContext);

    const [, setCreateFormOpen] = useState(false);
    const [, setEditQuestionFormOpen] = useState(false);
    const [, setCreateStrategyFormOpen] = useState(false);
    const [questionText, setQuestionText] = useState("");
    const questions = questionReponses;
    const strategies = strategieReponses;
    const [questionsTemp, setQuestionsTemp] = useState([]);

    useEffect(() => {
        if (questionReponses && questionReponses.parent_des_reponses) {
            let sortedQuestions = [...questionReponses.parent_des_reponses].sort(
                (a, b) => (a.no_reponse > b.no_reponse ? 1 : -1)
            );
            setQuestionsTemp(sortedQuestions);
        }
    }, [questionReponses]);

    const handleArrowClick = (event) => {
        event.preventDefault();

        let cardId = event.currentTarget.id.split("_");
        let body = {
            reponse1_id: 0,
            reponse2_id: 0
        };

        const indexReponse = questionsTemp.findIndex(
            element => element.id === parseInt(cardId[1], 10)
        );

        if (cardId[0] === "up" && indexReponse > 0) {
            body.reponse1_id = questionsTemp[indexReponse - 1].id;
            body.reponse2_id = parseInt(cardId[1], 10);
            switchOrderReponse(body);
        } else if (cardId[0] === "down" && indexReponse < (questionsTemp.length - 1)) {
            body.reponse1_id = parseInt(cardId[1], 10);
            body.reponse2_id = questionsTemp[indexReponse + 1].id;
            switchOrderReponse(body);
        }

        needReloadComponent(!reloadComponent);
    };

    useEffect(() => {
        const reponseTemp = [...questionsTemp];
        reponseTemp.sort((a, b) => a.no_reponse - b.no_reponse);

        let hasOther = false;
        let otherIndex = null;
        let highestIndex = 0;
        let usedIndexes = new Set();

        reponseTemp.forEach(reponse => {
            if (reponse.contenu === "[autre]") {
                hasOther = true;
                otherIndex = reponseTemp.indexOf(reponse);
            }
            if (reponse.no_reponse !== undefined) {
                usedIndexes.add(reponse.no_reponse);
                if (reponse.no_reponse > highestIndex) {
                    highestIndex = reponse.no_reponse;
                }
            }
        });

        if (hasOther && otherIndex !== null) {
            let newNoReponse = highestIndex;
            while (usedIndexes.has(newNoReponse)) {
                newNoReponse++;
            }
            reponseTemp[otherIndex] = {
                ...reponseTemp[otherIndex],
                no_reponse: newNoReponse
            };
        }

        if (state.locale === LOCALES.FRENCH) {
            setQuestionText(questions.contenu);
        } else if (state.locale === LOCALES.ENGLISH) {
            setQuestionText(questions.contenu_en);
        }
    }, [questionsTemp, questions, state.locale, needReloadComponent]);

    const normalResponses = questionsTemp.filter(
        (response) =>
            response.contenu !== "[autre]" &&
            response.contenu_en !== "[other]" &&
            response.contenu !== "Pour faciliter le repérage ultérieur, indiquer vos initiales – celles du patient (p. ex. CV-HC)" &&
            response.contenu_en !== "To facilitate later identification, indicate your initials – those of the patient (e.g., CV-HC)"
    );

    const customResponse = questionsTemp.find(
        (response) =>
            response.contenu === "[autre]" ||
            response.contenu_en === "[other]" ||
            response.contenu === "Pour faciliter le repérage ultérieur, indiquer vos initiales – celles du patient (p. ex. CV-HC)" ||
            response.contenu_en === "To facilitate later identification, indicate your initials – those of the patient (e.g., CV-HC)"
    );

    return (
        <div style={{ display: "grid", justifyItems: "center" }}>
            <h1
                id="mainQuestion"
                style={{
                    marginTop: "25px",
                    maxWidth: "60%",
                    gutterBottom: true,
                    align: "center",
                    color: "black",
                    fontSize: "36px",
                }}
            >
                {questionText}
                {isAdminRole && questionText !== "" && (
                    <IconButton
                        onClick={() => setEditQuestionFormOpen(true)}
                        aria-label="Edit"
                    >
                        <EditIcon sx={{ color: "black" }} />
                    </IconButton>
                )}
            </h1>

            <Container sx={{ py: 4 }}>
                {strategies[0]?.contenu !== "" && strategies[0]?.id !== "" &&
                    strategies.map((strategie) => (
                        <Grid item key={strategie.id} xs={2} sm={2} md={6}>
                            <CarteStrategie
                                strategie={strategie}
                                needReloadComponent={needReloadComponent}
                                reloadComponent={reloadComponent}
                            />
                        </Grid>
                    ))
                }

                {isAdminRole && !stateCheminDone && questionsTemp.length === 0 && (
                    <Button
                        onClick={() => setCreateStrategyFormOpen(true)}
                        variant="contained"
                        sx={{
                            backgroundColor: "#307B5B",
                            '&:hover': {
                                backgroundColor: "#8EBF55",
                                color: "black",
                                fontWeight: "bold",
                            },
                        }}
                        style={{ marginTop: "2rem", width: "12rem", marginRight: "1rem" }}
                    >
                        {translate("questionnaire.AjouterStrategie")}
                    </Button>
                )}

                {normalResponses.map((question) => (
                    <Grid item key={question.no_reponse} xs={2} sm={2} md={6}>
                        {question.question_sv_id ? (
                            <CarteQuestion
                                id={question.no_reponse}
                                question_id={questions.id}
                                reponse_id={question.id}
                                reponse_type={question.type_reponse}
                                text_en={question.contenu_en}
                                text={question.contenu}
                                arrowFunc={handleArrowClick}
                                needReloadComponent={needReloadComponent}
                                reloadComponent={reloadComponent}
                            />
                        ) : (
                            <CarteNewQuestion
                                id={question.no_reponse}
                                reponse_id={question.id}
                                reponse_type={question.type_reponse}
                                text_en={question.contenu_en}
                                text={question.contenu}
                                needReloadComponent={needReloadComponent}
                                reloadComponent={reloadComponent}
                            />
                        )}
                    </Grid>
                ))}

                {customResponse && (
                    <Grid item key={customResponse.no_reponse} xs={2} sm={2} md={6}>
                        <CarteQuestion
                            id={customResponse.no_reponse}
                            question_id={questions.id}
                            reponse_id={customResponse.id}
                            reponse_type={customResponse.type_reponse}
                            text_en={customResponse.contenu_en}
                            text={customResponse.contenu}
                            arrowFunc={handleArrowClick}
                            needReloadComponent={needReloadComponent}
                            reloadComponent={reloadComponent}
                        />
                    </Grid>
                )}

                {isAdminRole && !stateCheminDone && (
                    <Button
                        onClick={() => setCreateFormOpen(true)}
                        variant="contained"
                        sx={{
                            backgroundColor: "#307B5B",
                            '&:hover': {
                                backgroundColor: "#8EBF55",
                                color: "black",
                                fontWeight: "bold",
                            },
                        }}
                        style={{ marginTop: "2rem", width: "12rem" }}
                    >
                        {translate("questionnaire.AjouterReponse")}
                    </Button>
                )}
            </Container>
        </div>
    );
}
